<template>
  <div v-if="!isOwnerOrAgent" class="modal-card">
    <div class="box">
      <no-results
        :cta="{ label: 'Close' }"
        icon="user-shield"
        title="Invalid permissions"
        message="Orders can only be placed by site owners. Please ask the site owner to upgrade this subscription."
        @click="$emit('close')"
      />
    </div>
  </div>

  <modal-card v-else :title="step.label" :processing="processing">
    <div
      v-if="
        !$store.getters['auth/isCurrentUser'](userId) &&
          $store.getters['user/isAdmin']()
      "
      slot="subheader"
      class="has-background-warning has-padding-100 has-text-centered"
    >
      <p>
        <b-icon icon="shield-alt" size="is-small" />Tread carefully – you're
        acting on behalf of the customer.
      </p>
    </div>

    <loading v-if="loading" />

    <component
      :is="step.component"
      v-show="!loading"
      :user-id="userId"
      :cart="computedCart"
      :processing="processing"
      v-bind="step.props"
      @cartdata="cart = $_.merge(cart, $event)"
    />

    <button
      slot="footer"
      :disabled="!step.isValid() || processing"
      :class="{ 'is-loading': processing }"
      type="submit"
      class="button is-success"
      @click="nextStep"
    >
      Continue
    </button>
  </modal-card>
</template>

<script>
import { doc, getDoc } from "@firebase/firestore";
export default {
  name: "CheckoutModal",
  props: {
    productId: {
      type: String,
      required: true
    },
    productTermId: {
      type: String,
      required: true
    },
    siteId: {
      type: String,
      required: true
    },
    userId: {
      type: String,
      required: true
    },
    currency: {
      type: String,
      default: () => {
        return null;
      }
    }
  },
  data() {
    return {
      loading: true,
      processing: false,
      cart: {
        credit: null,
        paymentMethod: null,
        product: null,
        productTerm: null,
        termsAgreed: false,
        verifiedUser: false,
        couponId: null,
        siteId: this.siteId
      },
      stepNum: 0,
      steps: [
        {
          label: "Select payment method",
          component: () =>
            import("@shared/cart/_selectPaymentMethod").finally(() => {
              this.loading = false;
            }),
          isValid: () => {
            return this.$_.isObject(this.cart.paymentMethod);
          },
          props: {
            currency: this.currency
          }
        },
        {
          label: "Confirm order",
          component: () =>
            import("@shared/cart/_confirmPurchase").finally(() => {
              this.loading = false;
            }),
          isValid: () => {
            return this.cart.termsAgreed && !this.cart.hasError;
          }
        }
      ]
    };
  },
  computed: {
    isOwnerOrAgent() {
      return (
        this.$store.getters["user/isAgent"]() ||
        this.$store.getters["sites/site/userRole"](this.siteId) === "owner"
      );
    },
    step() {
      return this.steps[this.stepNum];
    },
    computedCart() {
      return this.$_.merge({}, this.cart, {
        credit: this.$store.getters["billing/credit"](this.userId)
      });
    }
  },
  created() {
    Promise.all([this.getProduct(), this.getProductTerm(), this.getCredit()])
      .then(() => {
        this.loading = false;
      })
      .catch(error => {
        this.$toast.open({
          message: `${error.message}`,
          position: "is-bottom",
          queue: false,
          type: "is-danger"
        });
      });
  },
  beforeDestroy() {
    this.$store.dispatch("billing/unobserveCredit", {
      userId: this.userId
    });
  },
  methods: {
    getProduct() {
      const productRef = doc(this.$firestore, "products", this.productId);
      return getDoc(productRef).then(product => {
        this.$set(
          this.cart,
          "product",
          this.$_.merge({}, { _id: product.id }, product.data())
        );
      });
    },
    getProductTerm() {
      const productTermRef = doc(
        this.$firestore,
        `products/${this.productId}/terms`,
        this.productTermId
      );
      return getDoc(productTermRef).then(productTerm => {
        this.$set(
          this.cart,
          "productTerm",
          this.$_.merge({}, { _id: productTerm.id }, productTerm.data())
        );
      });
    },
    getCredit() {
      return this.$store.dispatch("billing/observeCredit", {
        userId: this.userId
      });
    },
    nextStep() {
      if (this.stepNum < this.steps.length - 1) {
        this.$_.debounce(() => {
          this.loading = true;
        }, 250);
        this.stepNum++;
      } else {
        this.doOrder();
      }
    },
    doOrder() {
      if (this.processing) return;
      this.processing = true;
      this.$store
        .dispatch("billing/orderBasket", {
          userId: this.userId,
          siteId: this.siteId,
          productId: this.productId,
          productTermId: this.productTermId,
          paymentMethodId: this.cart.paymentMethod.id,
          couponId: this.cart.couponId
        })
        .then(result => {
          this.$emit("close");
          this.$toast.open({
            message: `${result.message}`
          });
        })
        .catch(error => {
          this.$toast.open({
            message: `${error}`,
            type: "is-danger"
          });
        })
        .finally(() => {
          this.processing = false;
        });
    }
  }
};
</script>
